import type { MediaList } from 'utils/webrtc/receiving/MediaList';
import type { AudioElement } from 'utils/webrtc';
import { logger } from 'utils/logger';
import {
  extractMediaStreamState,
  isStreamProblematic,
  MediaStreamDiagnostics,
} from './streamStateExtractor';

/**
 * Collects comprehensive diagnostics for all media streams (audio and video)
 *
 * Called after critical events (streaming-start, device changes, etc.) to capture
 * the complete state of media streams. Designed to be crash-proof since it runs
 * in timeouts and logging should never cause exceptions.
 *
 * Use this data to identify dead tracks, state mismatches, and where in the stack
 * media flow is breaking.
 *
 * Note: This function filters out local feeds (user's own publishing feed) from
 * diagnostics to avoid false positives. Local feeds exist in the MediaList for
 * technical reasons (streaming mode stopLocalStreams) but are never subscribed.
 */
export function logMediaStreamsDiagnostics(
  mediaList: MediaList,
  audioElements: Record<string, AudioElement>,
  videoElements: Record<string, HTMLVideoElement>,
  reason: string,
  localFeedIds?: Set<string>,
  filterKind?: 'audio' | 'video',
  changedStream?: { feedId: string; mid: string }
): void {
  let changedStreamDiagnostics: MediaStreamDiagnostics | undefined;
  const problematicStreams: MediaStreamDiagnostics[] = [];
  let healthyCount = 0;

  try {
    mediaList.forEachFeed((feed, feedId) => {
      // Skip local feeds to avoid false positives in diagnostics
      // Local feeds are in MediaList but never subscribed (handled by publishing feed)
      if (localFeedIds?.has(feedId)) {
        return;
      }

      feed.forEachStream((stream) => {
        try {
          if (!stream || (filterKind && stream.kind !== filterKind)) {
            return;
          }

          const { subscriberMid } = stream;
          let mediaElement: HTMLAudioElement | HTMLVideoElement | undefined;
          if (subscriberMid) {
            if (stream.kind === 'audio') {
              mediaElement =
                audioElements?.[subscriberMid] instanceof HTMLAudioElement
                  ? (audioElements[subscriberMid] as HTMLAudioElement)
                  : undefined;
            } else {
              mediaElement =
                videoElements?.[subscriberMid] instanceof HTMLVideoElement
                  ? videoElements[subscriberMid]
                  : undefined;
            }
          }

          const diagnostics = extractMediaStreamState(
            feedId,
            subscriberMid || '',
            stream,
            mediaElement
          );

          // Check if this is the changed stream
          if (
            changedStream &&
            feedId === changedStream.feedId &&
            subscriberMid === changedStream.mid
          ) {
            changedStreamDiagnostics = diagnostics;
          } else if (isStreamProblematic(diagnostics)) {
            problematicStreams.push(diagnostics);
          } else {
            healthyCount += 1;
          }
        } catch (error) {
          // Skip this stream if any error occurs
          logger
            .remote({ system: true })
            .warn('[STREAM-DIAGNOSTICS] Error collecting stream data', {
              error: error instanceof Error ? error.message : String(error),
            });
        }
      });
    });
  } catch (error) {
    logger.remote({ system: true }).error('[STREAM-DIAGNOSTICS] Error iterating feeds', {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  const totalStreams =
    (changedStreamDiagnostics ? 1 : 0) + problematicStreams.length + healthyCount;
  if (totalStreams > 0) {
    const kindLabel = filterKind ? filterKind.toUpperCase() : 'MEDIA';
    logger
      .remote({ system: true, capture: 'streaming' })
      .debug(`[${kindLabel}-DIAGNOSTICS] ${reason}`, {
        changedStream: changedStreamDiagnostics,
        problematicStreams: problematicStreams.length > 0 ? problematicStreams : undefined,
        healthyCount,
      });
  }
}
